import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { padDay, padMonth } from "@monorepo/helpers";
import theme from "@monorepo/styles/theme";
import { Typography } from "@mui/material";
import { Box } from "@mui/material";
import { format } from "date-fns";

interface IStyledSelect {
  isMonth?: boolean;
}

export const StyledSelect = styled("select")<IStyledSelect>(({ isMonth }) => ({
  ...(isMonth && {
    minWidth: "12rem",
  }),
  background: "none",
}));

export const DateSelect = ({
  date,
  onChange,
  label,
  hideDay,
  error,
  disabled,
  minYear,
  yearsToDisplay = 100,
}: {
  date: string;
  onChange: ({ dateVal, isFromMobile }: { dateVal: string; isFromMobile?: boolean }) => void;
  label?: string;
  hideDay?: boolean;
  error?: string;
  disabled?: boolean;
  yearsToDisplay?: number;
  minYear?: number;
}) => {
  const formattedDate = useMemo(() => new Date(format(new Date(date), "yyyy-MM-dd")), [date]);
  const year = useMemo(() => formattedDate.getFullYear().toString(), [formattedDate]);
  const month = useMemo(() => (formattedDate.getMonth() + 1).toString(), [formattedDate]);
  const day = useMemo(() => formattedDate.getDate().toString(), [formattedDate]);
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: "grid",
        gridRowGap: theme.spacing(6 / 8),
        ...(!!error && {
          color: theme.palette.error.dark,
          "& select": {
            borderColor: theme.palette.error.dark,
          },
        }),
      }}
    >
      {label && (
        <Typography
          component="label"
          sx={{ textAlign: "left", color: theme.palette.nobelGray.main }}
          htmlFor="date-select"
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, max-content)",
          gridColumnGap: theme.spacing(12 / 8),
        }}
      >
        <StyledSelect
          disabled={disabled}
          name="date"
          id="date-select"
          value={year}
          onChange={(e) => {
            onChange({
              dateVal: new Date(`${e.currentTarget.value}-${padMonth(month)}-${padDay(day || 1)}`).toLocaleDateString(
                "LT-lt"
              ),
            });
          }}
        >
          {Array.from({ length: yearsToDisplay })
            .fill(0)
            .map((_, index) => {
              const y = new Date().getFullYear() - index;
              if (minYear !== undefined && y < minYear) return null;

              return (
                <Typography
                  key={y}
                  value={String(y)}
                  component="option"
                  sx={{
                    textTransform: "capitalize",
                  }}
                >
                  {y}
                </Typography>
              );
            })}
        </StyledSelect>
        <StyledSelect
          isMonth
          disabled={disabled}
          value={month}
          onChange={(e) => {
            onChange({
              dateVal: new Date(`${year}-${padMonth(e.currentTarget.value)}-${padDay(day || 1)}`).toLocaleDateString(
                "LT-lt"
              ),
            });
          }}
        >
          {Array.from({ length: 12 })
            .fill(0)
            .map((_, index) => (
              <Typography
                key={padMonth(index + 1)}
                component="option"
                sx={{
                  textTransform: "capitalize",
                }}
                value={index + 1}
              >
                {t(`months.${padMonth(index + 1)}`)}
              </Typography>
            ))}
        </StyledSelect>
        {!hideDay && (
          <StyledSelect
            disabled={disabled}
            value={day}
            onChange={(e) => {
              onChange({
                dateVal: new Date(
                  `${year}-${padMonth(month)}-${padDay(e.currentTarget.value || 1)}`
                ).toLocaleDateString("LT-lt"),
              });
            }}
          >
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
            <option value="6">6</option>
            <option value="7">7</option>
            <option value="8">8</option>
            <option value="9">9</option>
            <option value="10">10</option>
            <option value="11">11</option>
            <option value="12">12</option>
            <option value="13">13</option>
            <option value="14">14</option>
            <option value="15">15</option>
            <option value="16">16</option>
            <option value="17">17</option>
            <option value="18">18</option>
            <option value="19">19</option>
            <option value="20">20</option>
            <option value="21">21</option>
            <option value="22">22</option>
            <option value="23">23</option>
            <option value="24">24</option>
            <option value="25">25</option>
            <option value="26">26</option>
            <option value="27">27</option>
            <option value="28">28</option>
            <option value="29">29</option>
            <option value="30">30</option>
            <option value="31">31</option>
          </StyledSelect>
        )}
      </Box>
      {error && (
        <Typography
          sx={{
            color: theme.palette.error.dark,
            fontSize: theme.typography.caption.fontSize,
            padding: `0 ${theme.spacing(12 / 8)} 0 0`,
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};
